import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, XCircle, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import * as XLSX from 'xlsx';
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface ImportSpreadsheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WizardStep = 1 | 2 | 3 | 4;

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ValidationWarning {
  row: number;
  field: string;
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

// Caracteres especiais proibidos
const FORBIDDEN_CHARS = /['\"!@#%¨&*()ªº§+_\-?°\[\]{}:;]/g;
const FORBIDDEN_CHARS_DISPLAY = "' \" ! @ # % ¨ & * ( ) ª º § + _ - ? ° [ { } ] : ;";

/**
 * Parse date from various formats (DD/MM/YYYY, Excel serial, ISO)
 */
function parseDate(value: any): string | null {
  if (!value) return null;

  // Excel serial number (numeric date)
  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${String(date.d).padStart(2, '0')}/${String(date.m).padStart(2, '0')}/${date.y}`;
    }
  }

  // String date in DD/MM/YYYY format
  if (typeof value === 'string') {
    // Already in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return value;
    }

    // ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      }
    }
  }

  return null;
}

// Validate Brazilian date (DD/MM/YYYY) or ISO (YYYY-MM-DD)
function isValidBrazilianOrISODate(value: string): boolean {
  if (!value) return false;
  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [d, m, y] = value.split('/').map(Number);
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  }
  // ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
  return false;
}

/**
 * Parse value field (handles Brazilian currency format)
 */
function parseValue(value: any): number {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    // Remove currency symbols and convert Brazilian format
    let cleaned = value.replace(/[R$\s]/g, '');
    // Replace comma with dot for decimal separator
    cleaned = cleaned.replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }

  return 0;
}

/**
 * Map Excel row to transaction data format
 */
interface ExcelRow {
  'Data de Competência': any;
  'Data de Vencimento': any;
  'Data de Pagamento': any;
  'Valor': any;
  'Categoria': any;
  'Descrição': any;
  'Cliente/Fornecedor': any;
  'Centro de Custo': any;
  'Observações': any;
}

function mapExcelRowToTransaction(row: ExcelRow): any {
  const competenceDate = parseDate(row['Data de Competência']);
  const dueDate = parseDate(row['Data de Vencimento']);
  const paymentDate = parseDate(row['Data de Pagamento']);
  const value = parseValue(row['Valor']);

  return {
    competence_date: competenceDate,
    due_date: dueDate,
    payment_date: paymentDate,
    value: value,
    category: row['Categoria']?.toString().trim() || '',
    description: row['Descrição']?.toString().trim() || '',
    client_supplier: row['Cliente/Fornecedor']?.toString().trim() || '',
    cost_center: row['Centro de Custo']?.toString().trim() || '',
    observations: row['Observações']?.toString().trim() || '',
    is_paid: false, // All imported transactions start as pending (pendente)
  };
}

/**
 * Parse Excel file and extract data from "dados" sheet
 */
async function parseExcelFile(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Não foi possível ler o arquivo'));
          return;
        }

        const workbook = XLSX.read(data, { type: 'binary', cellDates: false });

        // Find "dados" sheet (case insensitive)
        const sheetName = workbook.SheetNames.find(
          name => name.toLowerCase() === 'dados'
        );

        if (!sheetName) {
          reject(new Error('Planilha não contém uma aba "dados". Verifique o arquivo.'));
          return;
        }

        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(worksheet, {
          defval: '',
          raw: false, // Keep formatted values for dates
        });

        if (jsonData.length === 0) {
          reject(new Error('A aba "dados" está vazia'));
          return;
        }

        // Map Excel rows to transaction format
        const transactions = jsonData.map(mapExcelRowToTransaction);

        resolve(transactions);
      } catch (error: any) {
        reject(new Error(`Erro ao processar planilha: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };

    reader.readAsBinaryString(file);
  });
}

/**
 * Valida os dados da planilha importada
 */
function validateSpreadsheetData(data: any[]): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  data.forEach((row, index) => {
    const rowNumber = index + 1; // Começa do 1 para usuário

    // Validação de campos de texto (caracteres especiais e formatação)
    const textFields = [
      { key: 'description', label: 'Descrição' },
      { key: 'category', label: 'Categoria' },
      { key: 'client_supplier', label: 'Cliente/Fornecedor' },
      { key: 'observations', label: 'Observações' }
    ];

    textFields.forEach(({ key, label }) => {
      const value = row[key];

      if (value && typeof value === 'string') {
        // 1. Verificar fórmulas
        if (value.trim().startsWith('=')) {
          errors.push({
            row: rowNumber,
            field: label,
            message: `Fórmula detectada. Use apenas texto simples.`
          });
        }

        // 2. Verificar caracteres proibidos
        const forbiddenCharsFound = value.match(FORBIDDEN_CHARS);
        if (forbiddenCharsFound) {
          const uniqueChars = [...new Set(forbiddenCharsFound)].join(' ');
          errors.push({
            row: rowNumber,
            field: label,
            message: `Caracteres proibidos encontrados: ${uniqueChars}`
          });
        }

        // 3. Verificar espaços extras
        if (value !== value.trim()) {
          warnings.push({
            row: rowNumber,
            field: label,
            message: `Espaços extras no início ou fim do texto`
          });
        }

        // 4. Verificar espaços múltiplos
        if (/\s{2,}/.test(value)) {
          warnings.push({
            row: rowNumber,
            field: label,
            message: `Espaços múltiplos detectados`
          });
        }
      }
    });

    // Validar apenas se a data de pagamento é válida, não precisa validar status
    // pois todas as transações importadas começarão como pendentes
    const paymentDate = row.payment_date;

    if (paymentDate) {
      // Accept Brazilian format DD/MM/YYYY and ISO YYYY-MM-DD
      if (!isValidBrazilianOrISODate(paymentDate)) {
        errors.push({
          row: rowNumber,
          field: 'Data de Pagamento',
          message: `Data inválida: ${paymentDate}`
        });
      }
    }

    // Validação de valor
    if (!row.value || row.value === 0) {
      errors.push({
        row: rowNumber,
        field: 'Valor',
        message: 'Valor obrigatório e deve ser diferente de zero'
      });
    }

    // Validação de campos obrigatórios
    const requiredFields = [
      { key: 'competence_date', label: 'Data de Competência' },
      // Usar payment_date em vez de due_date pois este campo não existe na tabela
      { key: 'payment_date', label: 'Data de Pagamento' },
      { key: 'description', label: 'Descrição' }
    ];

    requiredFields.forEach(({ key, label }) => {
      if (!row[key] || (typeof row[key] === 'string' && row[key].trim() === '')) {
        errors.push({
          row: rowNumber,
          field: label,
          message: 'Campo obrigatório'
        });
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

export function ImportSpreadsheetDialog({ open, onOpenChange }: ImportSpreadsheetDialogProps) {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult>({ valid: true, errors: [], warnings: [] });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar formato
      const validFormats = ['.xls', '.xlsx'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validFormats.includes(fileExtension)) {
        toast.error("Formato inválido. Use XLS ou XLSX.");
        return;
      }

      // Validar tamanho (10 MB)
      const maxSize = 10 * 1024 * 1024; // 10 MB em bytes
      if (file.size > maxSize) {
        toast.error("Arquivo muito grande. Tamanho máximo: 10 MB.");
        return;
      }

      setSelectedFile(file);
      toast.success("Arquivo selecionado com sucesso!");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Selecione um arquivo primeiro.");
      return;
    }

    setIsProcessing(true);
    toast.loading("Processando planilha...", { id: "import-process" });

    try {
      // Parse Excel file
      const transactions = await parseExcelFile(selectedFile);

      // Validate parsed data
      const validation = validateSpreadsheetData(transactions);
      setValidationResult(validation);
      setParsedData(transactions);
      setCurrentStep(2);

      if (validation.errors.length > 0) {
        toast.error(`Planilha processada com ${validation.errors.length} erro(s). Revise antes de continuar.`, {
          id: "import-process",
        });
      } else if (validation.warnings.length > 0) {
        toast.warning(`Planilha processada com ${validation.warnings.length} aviso(s). Revise as sugestões.`, {
          id: "import-process",
        });
      } else {
        toast.success("Planilha carregada com sucesso!", { id: "import-process" });
      }
    } catch (error: any) {
      console.error("Erro ao processar planilha:", error);
      toast.error(error.message || "Erro ao processar planilha", {
        id: "import-process",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = () => {
    setCurrentStep(3);
  };

  const handleImport = async () => {
    setIsProcessing(true);
    toast.loading("Importando transações...", { id: "import-submit" });

    try {
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("Usuário não autenticado");
      }

      // Get or create default category for each transaction
      const getCategoryId = async (categoryName: string, type: 'income' | 'expense'): Promise<number> => {
        // Try to find existing category
        const { data: existingCategories, error: fetchError } = await supabase
          .from('categories')
          .select('id')
          .eq('name', categoryName)
          .eq('type', type)
          .eq('user_id', user.id)
          .limit(1);

        if (fetchError) {
          console.error('Error fetching category:', fetchError);
          throw fetchError;
        }

        if (existingCategories && existingCategories.length > 0) {
          console.log(`Using existing category: ${categoryName} (ID: ${existingCategories[0].id})`);
          return existingCategories[0].id;
        }

        // Create new category
        console.log(`Creating new category: ${categoryName} (${type})`);
        const { data: newCategory, error: createError } = await supabase
          .from('categories')
          .insert({
            name: categoryName,
            type: type,
            user_id: user.id,
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating category:', createError);
          throw createError;
        }

        console.log(`Created new category with ID: ${newCategory.id}`);
        return newCategory.id;
      };

      // Convert DD/MM/YYYY to ISO date
      const convertToISODate = (dateStr: string | null): string | null => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        // Convert DD/MM/YYYY to YYYY-MM-DD preserving day/month order
        return `${year}-${month}-${day}`;
      };

      // Prepare transactions for insertion
      const transactionsToInsert = await Promise.all(
        parsedData.map(async (row) => {
          const type: 'income' | 'expense' = row.value >= 0 ? 'income' : 'expense';
          const categoryId = await getCategoryId(row.category || 'Sem categoria', type);

          // All imported transactions start as pending (pendente)
          const isPaid = false;
          const paymentDate = row.payment_date;

          return {
            user_id: user.id,
            type: type,
            amount: Math.abs(row.value),
            description: row.description?.trim() || 'Sem descrição',
            category_id: categoryId,
            // Datas: salvar as três quando fornecidas (competência, vencimento, pagamento)
            date: convertToISODate(row.competence_date)!,
            competence_date: convertToISODate(row.competence_date),
            due_date: convertToISODate(row.due_date),
            is_paid: isPaid,
            payment_date: convertToISODate(row.payment_date), // Se existir, será gravada; o status continua pendente
            notes: row.observations?.trim() || null,
            supplier: type === 'expense' ? row.client_supplier?.trim() : null,
            client: type === 'income' ? row.client_supplier?.trim() : null,
            is_recurring: false,
            is_scheduled: false,
          };
        })
      );

      // Insert all transactions
      const { error: insertError } = await supabase
        .from('transactions')
        .insert(transactionsToInsert);

      if (insertError) throw insertError;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      setCurrentStep(4);
      toast.success(`${parsedData.length} transações importadas com sucesso!`, {
        id: "import-submit",
      });
    } catch (error: any) {
      console.error("Erro ao importar transações:", error);
      toast.error(error.message || "Erro ao importar transações", {
        id: "import-submit",
        description: "Tente novamente ou contate o suporte",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setParsedData([]);
    onOpenChange(false);
  };

  const steps = [
    { number: 1, label: "Carregar planilha" },
    { number: 2, label: "Cadastrar" },
    { number: 3, label: "Confirmar dados" },
    { number: 4, label: "Concluir" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Importar planilha com lançamentos
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="py-8 px-8">
          <div className="relative max-w-4xl mx-auto">
            {/* Container dos steps */}
            <div className="flex items-center justify-between relative">
              {steps.map((step, index) => (
                <div key={step.number} className="flex flex-col items-center gap-3 flex-1 relative">
                  {/* Linha conectora - aparece antes de cada step exceto o primeiro */}
                  {index > 0 && (
                    <div className="absolute top-6 right-1/2 w-full h-1 -z-10" style={{ transform: 'translateY(-50%)' }}>
                      <div
                        className={`h-full transition-all duration-500 ${
                          currentStep > step.number ? "bg-green-500" : "bg-border"
                        }`}
                      />
                    </div>
                  )}

                  {/* Círculo do step */}
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 relative z-10 ${
                      currentStep === step.number
                        ? "bg-primary text-primary-foreground shadow-lg ring-4 ring-primary/20"
                        : currentStep > step.number
                        ? "bg-green-500 text-white"
                        : "bg-card text-muted-foreground border-2 border-border"
                    }`}
                  >
                    {currentStep > step.number ? (
                      <CheckCircle2 className="w-7 h-7" />
                    ) : (
                      step.number
                    )}
                  </div>

                  {/* Label do step */}
                  <span
                    className={`text-sm font-medium text-center max-w-[120px] transition-colors ${
                      currentStep === step.number
                        ? "text-foreground font-semibold"
                        : currentStep > step.number
                        ? "text-green-600 font-medium"
                        : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step 1: Carregar Planilha */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Como importar seus lançamentos</h3>
              <p className="text-sm text-muted-foreground">
                A importação de lançamentos em planilha passa por três etapas: primeiro, você envia uma planilha,
                depois, confirma os dados encontrados e, por fim, finaliza a importação. O processo completo pode
                demorar alguns minutos e vamos precisar da sua ajuda em todas as etapas.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Faça download da planilha modelo</h3>

              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <FileSpreadsheet className="w-16 h-16 text-primary" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <p className="text-sm">
                      Para importar seus lançamentos a partir de uma planilha, ela precisa estar no{" "}
                      <span className="font-semibold">formato correto da planilha modelo</span> que disponibilizamos.
                      As planilhas serão importadas tanto para despesas como para receitas. Exemplo:
                    </p>

                    <div className="bg-background rounded border p-3 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            <th className="p-2 text-left bg-blue-100">Data de Competência</th>
                            <th className="p-2 text-left bg-blue-100">Data de Vencimento</th>
                            <th className="p-2 text-left bg-blue-100">Data de Pagamento</th>
                            <th className="p-2 text-left bg-blue-100">Valor</th>
                            <th className="p-2 text-left bg-blue-100">Categoria</th>
                            <th className="p-2 text-left bg-blue-100">Descrição</th>
                            <th className="p-2 text-left bg-blue-100">Cliente/Fornecedor</th>
                            <th className="p-2 text-left bg-blue-100">Centro de Custo</th>
                            <th className="p-2 text-left bg-blue-100">Observações</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-2">01/09/2020</td>
                            <td className="p-2">01/10/2020</td>
                            <td className="p-2">02/10/2020</td>
                            <td className="p-2">5.000,00</td>
                            <td className="p-2">Serviços Prestados</td>
                            <td className="p-2">Venda Ref. NF 89</td>
                            <td className="p-2">Costa Azul Software Ltda</td>
                            <td className="p-2">Administrativo</td>
                            <td className="p-2">Boleto 30 dias</td>
                          </tr>
                          <tr>
                            <td className="p-2">30/09/2020</td>
                            <td className="p-2">30/10/2020</td>
                            <td className="p-2"></td>
                            <td className="p-2">-2.800,00</td>
                            <td className="p-2">Aluguel</td>
                            <td className="p-2">Aluguel Sala 09/2020</td>
                            <td className="p-2">Folha Empreendimentos Ltda</td>
                            <td className="p-2">Administrativo</td>
                            <td className="p-2"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a href="/docs/Planilha_Modelo_Insert.xls" download="Planilha_Modelo_Insert.xls">
                        Fazer download
                      </a>
                    </Button>
                  </div>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <p className="font-semibold mb-2">O preenchimento dos campos é obrigatório, com exceção de:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Centro de custo</li>
                      <li>Cliente/Fornecedor</li>
                      <li>CPF/CNPJ nos casos de apenas um tipo papel (cliente, fornecedor ou transportadora)</li>
                      <li>Observações</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Confira as regras abaixo e selecione a planilha</h3>

              <ul className="text-sm space-y-2 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>Formatos permitidos: <span className="font-semibold">XLS e XLSX</span></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>Tamanho máximo: <span className="font-semibold">10 Mb</span></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    Todas as transações importadas serão cadastradas com status "Pendente" para conferência.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    O sinal de (-) indica valores a pagar, por isso é importante conferir se todas as despesas estão com símbolo de subtração.
                  </span>
                </li>
              </ul>

              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8">
                <div className="flex flex-col items-center gap-4">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <div className="text-center">
                    <p className="text-sm font-medium mb-1">
                      {selectedFile ? selectedFile.name : "Escolha um arquivo"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Ou arraste-o para este espaço
                    </p>
                  </div>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".xls,.xlsx"
                    onChange={handleFileSelect}
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    Escolha um arquivo
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile || isProcessing}>
                {isProcessing ? "Processando..." : "Enviar arquivo"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Cadastrar/Mapear */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* Status da validação */}
            {validationResult.errors.length === 0 && validationResult.warnings.length === 0 && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Planilha processada com sucesso! {parsedData.length} lançamentos encontrados sem erros.
                </AlertDescription>
              </Alert>
            )}

            {/* Erros críticos */}
            {validationResult.errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-900 font-semibold">
                  {validationResult.errors.length} erro{validationResult.errors.length > 1 ? 's' : ''} encontrado{validationResult.errors.length > 1 ? 's' : ''}
                </AlertTitle>
                <AlertDescription className="text-red-800">
                  <p className="mb-2">Corrija os erros abaixo antes de continuar:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {validationResult.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-sm">
                        <strong>Linha {error.row}</strong> - {error.field}: {error.message}
                      </div>
                    ))}
                    {validationResult.errors.length > 10 && (
                      <div className="text-sm font-semibold mt-2">
                        ... e mais {validationResult.errors.length - 10} erro(s)
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Warnings */}
            {validationResult.warnings.length > 0 && validationResult.errors.length === 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-900 font-semibold">
                  {validationResult.warnings.length} aviso{validationResult.warnings.length > 1 ? 's' : ''}
                </AlertTitle>
                <AlertDescription className="text-amber-800">
                  <p className="mb-2">Revise as sugestões abaixo (não impedem a importação):</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {validationResult.warnings.slice(0, 5).map((warning, index) => (
                      <div key={index} className="text-sm">
                        <strong>Linha {warning.row}</strong> - {warning.field}: {warning.message}
                      </div>
                    ))}
                    {validationResult.warnings.length > 5 && (
                      <div className="text-sm font-semibold mt-2">
                        ... e mais {validationResult.warnings.length - 5} aviso(s)
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Contador de validação */}
            <div className="flex items-center gap-4 text-sm bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>{parsedData.length} lançamentos</span>
              </div>
              {validationResult.errors.length > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-red-600 font-semibold">{validationResult.errors.length} erros</span>
                </div>
              )}
              {validationResult.warnings.length > 0 && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-600">{validationResult.warnings.length} avisos</span>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Revise os dados importados</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Confira se as informações estão corretas antes de prosseguir.
                {validationResult.errors.length > 0 && (
                  <span className="text-red-600 font-semibold"> Linhas com erro estão destacadas em vermelho.</span>
                )}
              </p>

              <TooltipProvider>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-3 text-left font-medium">Linha</th>
                        <th className="p-3 text-left font-medium">Data Comp.
                          <div className="text-[10px] font-normal text-muted-foreground">Competência</div>
                        </th>
                        <th className="p-3 text-left font-medium">Data Venc.
                          <div className="text-[10px] font-normal text-muted-foreground">Vencimento</div>
                        </th>
                        <th className="p-3 text-left font-medium">Data Pag.
                          <div className="text-[10px] font-normal text-muted-foreground">Pagamento</div>
                        </th>
                        <th className="p-3 text-left font-medium">Categoria</th>
                        <th className="p-3 text-left font-medium">Descrição</th>
                        <th className="p-3 text-left font-medium">Status Inicial</th>
                        <th className="p-3 text-right font-medium">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((row, index) => {
                        const rowNumber = index + 1;
                        const rowErrors = validationResult.errors.filter(e => e.row === rowNumber);
                        const rowWarnings = validationResult.warnings.filter(w => w.row === rowNumber);
                        const hasIssues = rowErrors.length > 0 || rowWarnings.length > 0;

                        return (
                          <Tooltip key={index}>
                            <TooltipTrigger asChild>
                              <tr
                                className={`border-t cursor-pointer ${
                                  rowErrors.length > 0
                                    ? 'bg-red-50 hover:bg-red-100'
                                    : rowWarnings.length > 0
                                    ? 'bg-amber-50 hover:bg-amber-100'
                                    : 'hover:bg-muted/50'
                                }`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    {rowErrors.length > 0 && <XCircle className="h-4 w-4 text-red-600" />}
                                    {rowErrors.length === 0 && rowWarnings.length > 0 && (
                                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                                    )}
                                    <span>{rowNumber}</span>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                                    {row.competence_date || '—'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <span className="inline-flex items-center rounded bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-inset ring-purple-200">
                                    {row.due_date || '—'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <span className="inline-flex items-center rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                    {row.payment_date || '—'}
                                  </span>
                                </td>
                                <td className="p-3">{row.category}</td>
                                <td className="p-3">{row.description}</td>
                                <td className="p-3">
                                  <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800">
                                    Pendente
                                  </span>
                                </td>
                                <td className={`p-3 text-right font-medium ${row.value > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(row.value))}
                                </td>
                              </tr>
                            </TooltipTrigger>
                            {hasIssues && (
                              <TooltipContent className="max-w-md">
                                {rowErrors.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="font-semibold text-red-600">Erros:</p>
                                    {rowErrors.map((error, idx) => (
                                      <div key={idx} className="text-xs">
                                        • {error.field}: {error.message}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {rowWarnings.length > 0 && (
                                  <div className="space-y-1 mt-2">
                                    <p className="font-semibold text-amber-600">Avisos:</p>
                                    {rowWarnings.map((warning, idx) => (
                                      <div key={idx} className="text-xs">
                                        • {warning.field}: {warning.message}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TooltipProvider>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(1)} disabled={isProcessing}>
                Voltar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={validationResult.errors.length > 0 || isProcessing}
              >
                Confirmar dados
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmar Dados */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Confirmação final</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Você está prestes a importar <span className="font-semibold">{parsedData.length} lançamentos</span>.
                Esta ação não pode ser desfeita.
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Resumo da importação:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Total de lançamentos: {parsedData.length}</li>
                  <li>• Receitas: {parsedData.filter(d => d.value > 0).length}</li>
                  <li>• Despesas: {parsedData.filter(d => d.value < 0).length}</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setCurrentStep(2)} disabled={isProcessing}>
                Voltar
              </Button>
              <Button onClick={handleImport} disabled={isProcessing}>
                {isProcessing ? "Importando..." : "Importar lançamentos"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Concluir */}
        {currentStep === 4 && (
          <div className="space-y-6 text-center py-8">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-2">Importação concluída!</h3>
              <p className="text-muted-foreground">
                {parsedData.length} lançamentos foram importados com sucesso.
              </p>
            </div>

            <Button onClick={handleClose} className="mt-6">
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
